import React, { useEffect, useMemo, useState } from 'react';
import { resolveApiBase } from '@/utils/apiBase';

type DbInfo = {
  host: string;
  port?: number;
  database: string;
};

type ServicesInfo = {
  corePort?: number;
  queuePort?: number;
  browserPort?: number;
  whatsappPort?: number;
  webPort?: number;
  browserHeadless?: boolean;
  enabled?: string[];
};

type TenantSettingsInfo = {
  snapshotCronEnabled?: boolean;
  snapshotSchedule?: {
    enabled: boolean;
    mode: 'disabled' | 'daily' | 'twice_daily';
    primaryTime: string;
    secondaryTime?: string;
  };
};

type ServiceStatus = {
  status?: string;
  port?: number;
  pid?: number;
  cpu?: number;
  memory?: number;
  uptime?: number;
  restarts?: number;
};

type TenantStatus = {
  id: string;
  status: string;
  services?: {
    core?: ServiceStatus | null;
    queue?: ServiceStatus | null;
    browser?: ServiceStatus | null;
    whatsapp?: ServiceStatus | null;
    web?: ServiceStatus | null;
  };
};

type TenantInfo = {
  id: string;
  name: string;
  cvcrmConfigured: boolean;
  db: DbInfo | null;
  services?: ServicesInfo | null;
  settings?: TenantSettingsInfo | null;
  status?: string;
  servicesStatus?: TenantStatus['services'];
};

type SnapshotStatusInfo = {
  tenantId: string;
  snapshotCronEnabled: boolean;
  pm2SnapshotCronEnv: string | null;
  schedule: {
    enabled: boolean;
    mode: 'disabled' | 'daily' | 'twice_daily';
    primaryTime: string;
    secondaryTime?: string;
  };
  latest: Record<string, string | null>;
  jobs: {
    pending: number;
    processing: number;
    failed: number;
    done: number;
  };
  runs: {
    latestSuccessDate: string | null;
    latestFailureDate: string | null;
    latestFailureError: string | null;
  };
};

type HostStats = {
  cpuPct?: number | null;
  totalMem?: number;
  freeMem?: number;
  usedMem?: number;
  usedMemPct?: number;
  loadAvg?: number[];
  uptimeSec?: number;
  timestamp?: string;
};

const MANAGER_API = resolveApiBase(import.meta.env.VITE_MANAGER_API, '/manager-api', 'http://localhost:8789');
const LOGO_URL = import.meta.env.VITE_LOGO_URL || '/logo-visaoalpha.png';
const MANAGER_TOKEN = (import.meta.env.VITE_MANAGER_TOKEN || '').trim();
const DEFAULT_POLL_INTERVAL_MS = 3000;
const DEFAULT_HOST_RAM_MB = 12 * 1024;

const getManagerAuthHeader = (): Record<string, string> | undefined => {
  const token = (typeof window !== 'undefined' && localStorage.getItem('va_token')) || MANAGER_TOKEN;
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

function buildWebLink(t: TenantInfo) {
  const webPort = t.services?.webPort || t.servicesStatus?.web?.port;
  if (!webPort) return null;
  return `http://localhost:${webPort}`;
}

export const AdminTenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [hostStats, setHostStats] = useState<HostStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [pollIntervalMs, setPollIntervalMs] = useState(DEFAULT_POLL_INTERVAL_MS);
  const [hostRamMb, setHostRamMb] = useState(DEFAULT_HOST_RAM_MB);
  const [form, setForm] = useState({
    tenantId: '',
    tenantName: '',
    dbHost: '127.0.0.1',
    dbPort: 3306,
    dbUser: 'root',
    dbPass: '',
    dbName: '',
    corePort: 3001,
    queuePort: 3020,
    browserPort: 3010,
    whatsappPort: 3030,
    webPort: 4174,
    browserHeadless: false,
  });
  const [creating, setCreating] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [actioning, setActioning] = useState<string | null>(null);
  const [lastLogs, setLastLogs] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState<string | null>(null);
  const [snapshotModalTenant, setSnapshotModalTenant] = useState<TenantInfo | null>(null);
  const [snapshotStatus, setSnapshotStatus] = useState<SnapshotStatusInfo | null>(null);
  const [snapshotStatusLoading, setSnapshotStatusLoading] = useState(false);
  const [snapshotScheduleDraft, setSnapshotScheduleDraft] = useState<SnapshotStatusInfo['schedule'] | null>(null);
  const [snapshotRunDate, setSnapshotRunDate] = useState(() => new Date().toISOString().slice(0, 10));

  const summaries = useMemo(() => {
    const totalTenants = tenants.length;
    const onlineTenants = tenants.filter((t) => t.status === 'online').length;
    const erroredTenants = tenants.filter((t) => t.status === 'errored').length;
    return { totalTenants, onlineTenants, erroredTenants };
  }, [tenants]);

  const loadTenants = async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
      setError(null);
    }
    const headers = getManagerAuthHeader();
    try {
      const [configuredRes, statusRes, machineRes] = await Promise.all([
        fetch(`${MANAGER_API}/tenants/configured`, { headers }),
        fetch(`${MANAGER_API}/tenants`, { headers }),
        fetch(`${MANAGER_API}/machine`, { headers }),
      ]);

      if (!configuredRes.ok) throw new Error('Falha ao carregar tenants');
      const configuredData = await configuredRes.json();

      const statusData = statusRes.ok ? await statusRes.json() : { tenants: [] };
      const statusMap = new Map<string, TenantStatus>();
      (statusData.tenants || []).forEach((t: TenantStatus) => statusMap.set(t.id, t));
      if (machineRes.ok) {
        const machineData = await machineRes.json();
        setHostStats(machineData?.stats || null);
      }

      const merged = (configuredData.tenants || []).map((t: TenantInfo) => {
        const st = statusMap.get(t.id);
        return {
          ...t,
          status: st?.status,
          servicesStatus: st?.services,
        };
      });

      setTenants(merged);
      setLastUpdatedAt(new Date());
    } catch (err: any) {
      if (!options?.silent) {
        setError(err.message || 'Erro ao carregar tenants');
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      const headers = getManagerAuthHeader();
      try {
        const res = await fetch(`${MANAGER_API}/config`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (Number.isFinite(data?.pollIntervalMs)) {
          setPollIntervalMs(Number(data.pollIntervalMs));
        }
        if (Number.isFinite(data?.hostRamMb)) {
          setHostRamMb(Number(data.hostRamMb));
        }
      } catch {
        // Mantem defaults
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    loadTenants();
    const timer = setInterval(() => {
      loadTenants({ silent: true });
    }, pollIntervalMs);
    return () => clearInterval(timer);
  }, [pollIntervalMs]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setCreating(true);
    try {
      const body = {
        tenantId: form.tenantId,
        tenantName: form.tenantName || form.tenantId,
        db: {
          host: form.dbHost,
          port: Number(form.dbPort),
          user: form.dbUser,
          password: form.dbPass,
          database: form.dbName || `visao_${form.tenantId.toLowerCase()}`,
        },
        services: {
          corePort: Number(form.corePort) || undefined,
          queuePort: Number(form.queuePort) || undefined,
          browserPort: Number(form.browserPort) || undefined,
          whatsappPort: Number(form.whatsappPort) || undefined,
          webPort: Number(form.webPort) || undefined,
          browserHeadless: form.browserHeadless,
          enabled: ['core', 'queue', 'whatsapp', 'browser', 'web'],
        },
      };

      const res = await fetch(`${MANAGER_API}/tenants/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getManagerAuthHeader(),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Erro ao criar tenant');
      }
      setSuccessMsg(data.message || 'Tenant criado');
      await loadTenants({ silent: true });
    } catch (err: any) {
      setError(err.message || 'Erro ao criar tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleAction = async (tenantId: string, action: 'start' | 'stop' | 'restart' | 'migrate') => {
    setActioning(`${tenantId}-${action}`);
    setError(null);
    setSuccessMsg(null);
    setLastLogs(null);
    try {
      const res = await fetch(`${MANAGER_API}/tenants/${tenantId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getManagerAuthHeader(),
        },
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Falha ao ${action}`);
      }
      if (action === 'migrate') {
        setLastLogs((data.stdout || data.stderr) ? `${data.stdout || ''}\n${data.stderr || ''}`.trim() : null);
        setSuccessMsg(`Migrations executadas para ${tenantId}.`);
      } else {
        setSuccessMsg(`Tenant ${tenantId} ${action === 'start' ? 'iniciado' : action === 'stop' ? 'parado' : 'reiniciado'}.`);
      }
      await loadTenants({ silent: true });
    } catch (err: any) {
      setError(err.message || `Erro ao ${action}`);
    } finally {
      setActioning(null);
    }
  };

  const handleLogoUpload = async (tenantId: string, file: File) => {
    setError(null);
    setSuccessMsg(null);
    setLogoUploading(tenantId);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await fetch(`${MANAGER_API}/tenants/${tenantId}/logo`, {
        method: 'POST',
        headers: {
          ...getManagerAuthHeader(),
        },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Falha ao enviar logo.');
      setSuccessMsg(`Logo atualizado para ${tenantId}.`);
    } catch (err: any) {
      setError(err?.message || 'Falha ao enviar logo.');
    } finally {
      setLogoUploading(null);
    }
  };

  const handleBrowserControl = async (tenantId: string, action: 'start' | 'stop') => {
    setActioning(`${tenantId}-browser-${action}`);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${MANAGER_API}/tenants/${tenantId}/browser/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getManagerAuthHeader(),
        },
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Falha ao ${action} browser`);
      }
      setSuccessMsg(`Browser ${action === 'start' ? 'iniciado' : 'parado'} para ${tenantId}.`);
      await loadTenants({ silent: true });
    } catch (err: any) {
      setError(err.message || `Erro ao ${action} browser`);
    } finally {
      setActioning(null);
    }
  };

  const openSnapshotModal = async (tenant: TenantInfo) => {
    setSnapshotModalTenant(tenant);
    setSnapshotStatus(null);
    setSnapshotScheduleDraft(null);
    setSnapshotRunDate(new Date().toISOString().slice(0, 10));
    setSnapshotStatusLoading(true);
    setError(null);
    try {
      const res = await fetch(`${MANAGER_API}/tenants/${tenant.id}/snapshots`, {
        headers: {
          ...getManagerAuthHeader(),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Falha ao carregar status de snapshots');
      setSnapshotStatus(data as SnapshotStatusInfo);
      setSnapshotScheduleDraft((data as SnapshotStatusInfo).schedule);
    } catch (err: any) {
      setError(err?.message || 'Falha ao carregar status de snapshots');
    } finally {
      setSnapshotStatusLoading(false);
    }
  };

  const handleSnapshotCronToggle = async (tenant: TenantInfo, enabled: boolean) => {
    setActioning(`${tenant.id}-snapshot-cron`);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${MANAGER_API}/tenants/${tenant.id}/snapshot-cron`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getManagerAuthHeader(),
        },
        body: JSON.stringify({ enabled, restartCore: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Falha ao atualizar cron de snapshots');
      setSuccessMsg(data?.message || `Cron de snapshots ${enabled ? 'ativado' : 'desativado'} para ${tenant.id}.`);
      await loadTenants({ silent: true });
      await openSnapshotModal(tenant);
    } catch (err: any) {
      setError(err?.message || 'Falha ao atualizar cron de snapshots');
    } finally {
      setActioning(null);
    }
  };

  const handleSnapshotScheduleSave = async (tenant: TenantInfo) => {
    if (!snapshotScheduleDraft) return;
    setActioning(`${tenant.id}-snapshot-schedule`);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${MANAGER_API}/tenants/${tenant.id}/snapshot-cron`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getManagerAuthHeader(),
        },
        body: JSON.stringify({
          enabled: snapshotScheduleDraft.enabled,
          mode: snapshotScheduleDraft.mode,
          primaryTime: snapshotScheduleDraft.primaryTime,
          secondaryTime: snapshotScheduleDraft.secondaryTime || undefined,
          restartCore: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Falha ao salvar agendamento de snapshots');
      setSuccessMsg(data?.message || `Agendamento de snapshots salvo para ${tenant.id}.`);
      await loadTenants({ silent: true });
      await openSnapshotModal(tenant);
    } catch (err: any) {
      setError(err?.message || 'Falha ao salvar agendamento de snapshots');
    } finally {
      setActioning(null);
    }
  };

  const handleSnapshotRunNow = async (tenant: TenantInfo) => {
    setActioning(`${tenant.id}-snapshot-run-now`);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await fetch(`${MANAGER_API}/tenants/${tenant.id}/snapshots/run-now`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getManagerAuthHeader(),
        },
        body: JSON.stringify({ snapshotDate: snapshotRunDate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Falha ao executar snapshots manualmente');
      setSuccessMsg(data?.message || `Snapshots enfileirados para ${tenant.id}.`);
      await openSnapshotModal(tenant);
    } catch (err: any) {
      setError(err?.message || 'Falha ao executar snapshots manualmente');
    } finally {
      setActioning(null);
    }
  };

  const formatCpu = (value?: number | null) => (value === null || value === undefined ? '-' : `${value.toFixed(1)}%`);
  const formatBytes = (value?: number) => {
    if (value === null || value === undefined) return '-';
    const mb = value / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(0)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };
  const formatUptime = (value?: number) => {
    if (!value) return '-';
    const elapsed = Math.max(0, Date.now() - value);
    const hours = Math.floor(elapsed / 3_600_000);
    const minutes = Math.floor((elapsed % 3_600_000) / 60_000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    const seconds = Math.floor((elapsed % 60_000) / 1000);
    return `${minutes}m ${seconds}s`;
  };
  const formatUptimeSeconds = (seconds?: number) => {
    if (seconds === null || seconds === undefined) return '-';
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m ${secs}s`;
  };
  const toTenantTotals = (services?: TenantStatus['services']) => {
    const all = Object.values(services || {}).filter(Boolean) as ServiceStatus[];
    return {
      cpu: all.reduce((sum, svc) => sum + (svc.cpu || 0), 0),
      memory: all.reduce((sum, svc) => sum + (svc.memory || 0), 0),
      restarts: all.reduce((sum, svc) => sum + (svc.restarts || 0), 0),
      online: all.filter((svc) => svc.status === 'online').length,
      total: all.length,
      uptime: all.map((svc) => svc.uptime || 0).filter(Boolean).sort((a, b) => a - b)[0],
    };
  };

  return (
    <div className="min-h-screen bg-vision-darkGreen text-gray-100">
      <div className="px-6 py-8 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-vision-surface border border-vision-border flex items-center justify-center shadow-[0_0_24px_rgba(41,75,41,0.35)]">
              <img
                src={LOGO_URL}
                alt="Visao Alpha"
                className="h-7 w-auto"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-vision-green">Control Room</div>
              <h1 className="text-2xl font-semibold text-gray-100">Visao Alpha Control</h1>
              <div className="text-xs text-gray-400">
                {lastUpdatedAt ? `Atualizado ha ${Math.max(0, Math.floor((Date.now() - lastUpdatedAt.getTime()) / 1000))}s` : 'Aguardando atualizacao'}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => loadTenants()}
              className="px-4 py-2 rounded-lg bg-vision-green/20 text-vision-green border border-vision-border hover:bg-vision-green/30 transition"
            >
              Recarregar
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('va_token');
                window.location.reload();
              }}
              className="px-4 py-2 rounded-lg bg-vision-surface text-gray-200 border border-vision-border hover:border-vision-green transition"
            >
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded border border-vision-activeRed/50 bg-vision-activeRed/20 text-vision-activeRed text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="p-3 rounded border border-vision-green/40 bg-vision-green/15 text-vision-green text-sm whitespace-pre-wrap">
            {successMsg}
            {lastLogs && <pre className="mt-2 text-xs text-gray-100 bg-vision-surface border border-vision-border rounded p-2 overflow-auto max-h-40">{lastLogs}</pre>}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-vision-border bg-vision-surface p-4 shadow-[0_0_30px_rgba(10,26,10,0.6)]">
                <div className="text-xs uppercase tracking-[0.3em] text-vision-green">Tenants</div>
                <div className="text-3xl font-semibold text-gray-100">{summaries.totalTenants}</div>
                <div className="text-xs text-gray-400">Total configurados</div>
              </div>
              <div className="rounded-2xl border border-vision-border bg-vision-surface p-4 shadow-[0_0_30px_rgba(10,26,10,0.6)]">
                <div className="text-xs uppercase tracking-[0.3em] text-vision-green">Online</div>
                <div className="text-3xl font-semibold text-gray-100">{summaries.onlineTenants}</div>
                <div className="text-xs text-gray-400">Ativos no PM2</div>
              </div>
              <div className="rounded-2xl border border-vision-border bg-vision-surface p-4 shadow-[0_0_30px_rgba(10,26,10,0.6)]">
                <div className="text-xs uppercase tracking-[0.3em] text-vision-activeRed">Alertas</div>
                <div className="text-3xl font-semibold text-gray-100">{summaries.erroredTenants}</div>
                <div className="text-xs text-gray-400">Tenants em erro</div>
              </div>
              <div className="rounded-2xl border border-vision-border bg-vision-surface p-4 shadow-[0_0_30px_rgba(10,26,10,0.6)]">
                <div className="text-xs uppercase tracking-[0.3em] text-vision-green">Maquina</div>
                <div className="text-lg font-semibold text-gray-100">
                  CPU {formatCpu(hostStats?.cpuPct)}
                </div>
                <div className="text-xs text-gray-400">
                  RAM {formatBytes(hostStats?.usedMem)} / {formatBytes(hostStats?.totalMem)}
                </div>
                <div className="text-[11px] text-gray-500">
                  Uptime {formatUptimeSeconds(hostStats?.uptimeSec)}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-vision-border bg-vision-surface p-4 shadow-[0_0_40px_rgba(10,26,10,0.6)]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-100">Tenants Configurados</h2>
                {loading && <span className="text-xs text-gray-400">Carregando...</span>}
              </div>
              <div className="space-y-4">
                {tenants.map((t) => {
                  const overallOnline = t.status === 'online';
                  const dot = (status?: string) =>
                    status === 'online'
                      ? 'bg-vision-green shadow-[0_0_10px_rgba(41,75,41,0.6)]'
                      : status === 'errored'
                      ? 'bg-vision-activeRed shadow-[0_0_10px_rgba(139,0,0,0.6)]'
                      : 'bg-vision-border';
                  const svc = t.servicesStatus;
                  const totals = toTenantTotals(svc);
                  const cpuCap = Math.min(100, totals.cpu);
                  const ramMb = totals.memory / (1024 * 1024);
                  const ramCap = Math.min(100, (ramMb / hostRamMb) * 100);
                  const browserOnline = svc?.browser?.status === 'online';

                  return (
                    <div key={t.id} className="rounded-2xl border border-vision-border bg-vision-darkGreen p-4 shadow-[0_0_24px_rgba(10,26,10,0.6)]">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="text-lg font-semibold text-gray-100 flex items-center gap-2">
                              <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot(t.status)}`} />
                              {t.id}
                            </div>
                            <div className="text-sm text-gray-400">{t.name}</div>
                            <div className="text-xs text-gray-500">
                              DB: {t.db?.host}:{t.db?.port} / {t.db?.database}
                            </div>
                            <div className="text-xs text-gray-500">
                              Snapshot Cron: {t.settings?.snapshotCronEnabled === false ? 'desativado' : 'ativado'}
                            </div>
                            {buildWebLink(t) && (
                              <div className="text-xs text-vision-green">
                                Web: <a className="underline" href={buildWebLink(t)!} target="_blank" rel="noreferrer">{buildWebLink(t)}</a>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="text-xs uppercase tracking-[0.3em] text-gray-400">
                              {t.status ? t.status : 'desconhecido'}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                              {overallOnline ? (
                                <button
                                  onClick={() => handleAction(t.id, 'stop')}
                                  disabled={actioning !== null}
                                  className="text-xs px-3 py-1 rounded bg-vision-activeRed/20 text-vision-activeRed border border-vision-activeRed/40 hover:bg-vision-activeRed/30 disabled:opacity-60"
                                >
                                  {actioning === `${t.id}-stop` ? 'Parando...' : 'Stop'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleAction(t.id, 'start')}
                                  disabled={actioning !== null}
                                  className="text-xs px-3 py-1 rounded bg-vision-green text-white border border-vision-border hover:bg-vision-green/80 disabled:opacity-60"
                                >
                                  {actioning === `${t.id}-start` ? 'Iniciando...' : 'Start'}
                                </button>
                              )}
                              <button
                                onClick={() => handleAction(t.id, 'restart')}
                                disabled={actioning !== null}
                                className="text-xs px-3 py-1 rounded bg-vision-green text-white border border-vision-border hover:bg-vision-green/80 disabled:opacity-60"
                              >
                                {actioning === `${t.id}-restart` ? 'Reiniciando...' : 'Restart'}
                              </button>
                              {browserOnline ? (
                                <button
                                  onClick={() => handleBrowserControl(t.id, 'stop')}
                                  disabled={actioning !== null}
                                  className="text-xs px-3 py-1 rounded bg-vision-activeRed/20 text-vision-activeRed border border-vision-activeRed/40 hover:bg-vision-activeRed/30 disabled:opacity-60"
                                >
                                  {actioning === `${t.id}-browser-stop` ? 'Parando Browser...' : 'Browser Stop'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBrowserControl(t.id, 'start')}
                                  disabled={actioning !== null}
                                  className="text-xs px-3 py-1 rounded bg-vision-green text-white border border-vision-border hover:bg-vision-green/80 disabled:opacity-60"
                                >
                                  {actioning === `${t.id}-browser-start` ? 'Iniciando Browser...' : 'Browser Start'}
                                </button>
                              )}
                              <button
                                onClick={() => handleAction(t.id, 'migrate')}
                                disabled={actioning !== null}
                                className="text-xs px-3 py-1 rounded bg-vision-surface text-gray-200 border border-vision-border hover:border-vision-green disabled:opacity-60"
                                title="Executa pnpm migrate com TENANT configurado"
                              >
                                {actioning === `${t.id}-migrate` ? 'Migrando...' : 'Migrate'}
                              </button>
                              <button
                                onClick={() => openSnapshotModal(t)}
                                disabled={actioning !== null}
                                className="text-xs px-3 py-1 rounded bg-vision-surface text-gray-200 border border-vision-border hover:border-vision-green disabled:opacity-60"
                                title="Status e configuracao de snapshots"
                              >
                                Snapshots
                              </button>
                              <label className="text-xs px-3 py-1 rounded bg-vision-border/40 text-gray-100 border border-vision-border hover:border-vision-green cursor-pointer">
                                {logoUploading === t.id ? 'Enviando Logo...' : 'Enviar Logo'}
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                  className="hidden"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (!file) return;
                                    handleLogoUpload(t.id, file);
                                    event.currentTarget.value = '';
                                  }}
                                  disabled={logoUploading !== null}
                                />
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-4 gap-3">
                          <div className="rounded-xl border border-vision-border bg-vision-surface p-3">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-vision-green">CPU Total</div>
                            <div className="text-lg font-semibold text-gray-100">{formatCpu(totals.cpu)}</div>
                            <div className="mt-2 h-1.5 rounded-full bg-vision-border overflow-hidden">
                              <div className="h-full bg-vision-green" style={{ width: `${cpuCap}%` }} />
                            </div>
                          </div>
                          <div className="rounded-xl border border-vision-border bg-vision-surface p-3">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-vision-green">RAM Total</div>
                            <div className="text-lg font-semibold text-gray-100">{formatBytes(totals.memory)}</div>
                            <div className="mt-2 h-1.5 rounded-full bg-vision-border overflow-hidden">
                              <div className="h-full bg-vision-green/70" style={{ width: `${ramCap}%` }} />
                            </div>
                            <div className="text-[10px] text-gray-500 mt-1">Ref: host {Math.round(hostRamMb / 1024)}GB</div>
                          </div>
                          <div className="rounded-xl border border-vision-border bg-vision-surface p-3">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Servicos</div>
                            <div className="text-lg font-semibold text-gray-100">{totals.online}/{totals.total}</div>
                            <div className="text-xs text-gray-500">Online/Total</div>
                          </div>
                          <div className="rounded-xl border border-vision-border bg-vision-surface p-3">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-vision-activeRed">Uptime</div>
                            <div className="text-lg font-semibold text-gray-100">{formatUptime(totals.uptime)}</div>
                            <div className="text-xs text-gray-500">Restarts: {totals.restarts}</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs text-gray-300">
                          {([
                            ['Core', svc?.core],
                            ['Queue', svc?.queue],
                            ['Browser', svc?.browser],
                            ['WhatsApp', svc?.whatsapp],
                            ['Web', svc?.web],
                          ] as const).map(([label, service]) => (
                            <div key={label} className="rounded-lg border border-vision-border bg-vision-surface p-2">
                              <div className="flex items-center gap-2">
                                <span className={`inline-block h-2 w-2 rounded-full ${dot(service?.status)}`} />
                                <span className="text-gray-200">{label}</span>
                              </div>
                              <div className="mt-1 text-[11px] text-gray-500">
                                {service?.port ? `Porta ${service.port}` : 'Sem porta'}
                              </div>
                              <div className="mt-1 text-[11px] text-gray-400">
                                CPU {formatCpu(service?.cpu)} | RAM {formatBytes(service?.memory)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!loading && tenants.length === 0 && (
                  <div className="text-sm text-gray-500">Nenhum tenant configurado ainda.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-vision-border bg-vision-surface p-4 shadow-[0_0_40px_rgba(10,26,10,0.6)]">
            <h2 className="font-semibold text-gray-100 mb-3">Criar Tenant</h2>
            <form className="space-y-3" onSubmit={handleCreate}>
              <div>
                <label className="text-sm font-semibold text-gray-300">Tenant ID</label>
                <input
                  className="w-full mt-1 px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                  value={form.tenantId}
                  onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
                  placeholder="Ex: MEUTENANT"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-300">Nome</label>
                <input
                  className="w-full mt-1 px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                  value={form.tenantName}
                  onChange={(e) => setForm({ ...form, tenantName: e.target.value })}
                  placeholder="Ex: Meu Tenant"
                />
              </div>

              <div className="pt-2 border-t border-vision-border">
                <div className="text-sm font-semibold text-gray-300 mb-1">Banco</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    value={form.dbHost}
                    onChange={(e) => setForm({ ...form, dbHost: e.target.value })}
                    placeholder="host"
                  />
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    type="number"
                    value={form.dbPort}
                    onChange={(e) => setForm({ ...form, dbPort: Number(e.target.value) })}
                    placeholder="3306"
                  />
                </div>
                <input
                  className="w-full mt-2 px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                  value={form.dbUser}
                  onChange={(e) => setForm({ ...form, dbUser: e.target.value })}
                  placeholder="usuario"
                />
                <input
                  className="w-full mt-2 px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                  type="password"
                  value={form.dbPass}
                  onChange={(e) => setForm({ ...form, dbPass: e.target.value })}
                  placeholder="senha"
                />
                <input
                  className="w-full mt-2 px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                  value={form.dbName}
                  onChange={(e) => setForm({ ...form, dbName: e.target.value })}
                  placeholder="visao_meutenant"
                />
              </div>

              <div className="pt-2 border-t border-vision-border">
                <div className="text-sm font-semibold text-gray-300 mb-1">Portas dos servicos</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    type="number"
                    value={form.corePort}
                    onChange={(e) => setForm({ ...form, corePort: Number(e.target.value) })}
                    placeholder="Core (ex: 3001)"
                  />
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    type="number"
                    value={form.queuePort}
                    onChange={(e) => setForm({ ...form, queuePort: Number(e.target.value) })}
                    placeholder="Queue (ex: 3020)"
                  />
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    type="number"
                    value={form.browserPort}
                    onChange={(e) => setForm({ ...form, browserPort: Number(e.target.value) })}
                    placeholder="Browser (ex: 3010)"
                  />
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    type="number"
                    value={form.whatsappPort}
                    onChange={(e) => setForm({ ...form, whatsappPort: Number(e.target.value) })}
                    placeholder="WhatsApp (ex: 3030)"
                  />
                  <input
                    className="px-3 py-2 border border-vision-border bg-vision-darkGreen rounded-lg text-gray-100"
                    type="number"
                    value={form.webPort}
                    onChange={(e) => setForm({ ...form, webPort: Number(e.target.value) })}
                    placeholder="Web (ex: 4174)"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={creating}
                className="w-full mt-2 py-2 rounded-lg bg-vision-green/20 text-vision-green border border-vision-border font-semibold hover:bg-vision-green/30 transition disabled:opacity-60"
              >
                {creating ? 'Criando...' : 'Criar Tenant'}
              </button>
            </form>
          </div>
        </div>

        {snapshotModalTenant && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-3xl rounded-2xl border border-vision-border bg-vision-surface shadow-[0_0_40px_rgba(10,26,10,0.7)]">
              <div className="flex items-center justify-between border-b border-vision-border px-5 py-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-vision-green">Snapshots</div>
                  <h3 className="text-lg font-semibold text-gray-100">{snapshotModalTenant.id}</h3>
                </div>
                <button
                  onClick={() => setSnapshotModalTenant(null)}
                  className="rounded-lg border border-vision-border px-3 py-1 text-xs text-gray-200 hover:border-vision-green"
                >
                  Fechar
                </button>
              </div>
              <div className="space-y-4 px-5 py-4">
                {snapshotStatusLoading ? (
                  <div className="text-sm text-gray-400">Carregando status...</div>
                ) : snapshotStatus ? (
                  <>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="text-sm text-gray-200">
                        Cron configurado: <span className="font-semibold">{snapshotStatus.snapshotCronEnabled ? 'ativado' : 'desativado'}</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        Env PM2 (core): <span className="font-semibold text-gray-200">{snapshotStatus.pm2SnapshotCronEnv || '-'}</span>
                      </div>
                      <button
                        onClick={() => handleSnapshotCronToggle(snapshotModalTenant, !snapshotStatus.snapshotCronEnabled)}
                        disabled={actioning !== null}
                        className="ml-auto rounded-lg border border-vision-border px-3 py-1 text-xs text-gray-100 hover:border-vision-green disabled:opacity-60"
                      >
                        {actioning === `${snapshotModalTenant.id}-snapshot-cron`
                          ? 'Salvando...'
                          : snapshotStatus.snapshotCronEnabled
                          ? 'Desativar Cron'
                          : 'Ativar Cron'}
                      </button>
                    </div>

                    {snapshotScheduleDraft && (
                      <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-3 space-y-3">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <label className="text-xs text-gray-300">
                            <div className="mb-1 uppercase tracking-[0.2em] text-gray-400">Frequencia</div>
                            <select
                              className="w-full rounded-lg border border-vision-border bg-vision-surface px-3 py-2 text-sm text-gray-100"
                              value={snapshotScheduleDraft.mode}
                              onChange={(e) => {
                                const mode = e.target.value as SnapshotStatusInfo['schedule']['mode'];
                                setSnapshotScheduleDraft((prev) => prev ? {
                                  ...prev,
                                  mode,
                                  enabled: mode !== 'disabled',
                                  secondaryTime: mode === 'twice_daily' ? (prev.secondaryTime || '14:00') : undefined,
                                } : prev);
                              }}
                            >
                              <option value="disabled">Desativado</option>
                              <option value="daily">1x ao dia</option>
                              <option value="twice_daily">2x ao dia</option>
                            </select>
                          </label>
                          <label className="text-xs text-gray-300">
                            <div className="mb-1 uppercase tracking-[0.2em] text-gray-400">Horario principal</div>
                            <input
                              type="time"
                              className="w-full rounded-lg border border-vision-border bg-vision-surface px-3 py-2 text-sm text-gray-100"
                              value={snapshotScheduleDraft.primaryTime}
                              onChange={(e) => setSnapshotScheduleDraft((prev) => prev ? { ...prev, primaryTime: e.target.value } : prev)}
                            />
                          </label>
                          {snapshotScheduleDraft.mode === 'twice_daily' && (
                            <label className="text-xs text-gray-300">
                              <div className="mb-1 uppercase tracking-[0.2em] text-gray-400">Segundo horario</div>
                              <input
                                type="time"
                                className="w-full rounded-lg border border-vision-border bg-vision-surface px-3 py-2 text-sm text-gray-100"
                                value={snapshotScheduleDraft.secondaryTime || '14:00'}
                                onChange={(e) => setSnapshotScheduleDraft((prev) => prev ? { ...prev, secondaryTime: e.target.value } : prev)}
                              />
                            </label>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => handleSnapshotScheduleSave(snapshotModalTenant)}
                            disabled={actioning !== null}
                            className="rounded-lg border border-vision-border px-3 py-1 text-xs text-gray-100 hover:border-vision-green disabled:opacity-60"
                          >
                            {actioning === `${snapshotModalTenant.id}-snapshot-schedule` ? 'Salvando agenda...' : 'Salvar agenda'}
                          </button>
                          <div className="text-xs text-gray-400">
                            Agenda atual: <span className="text-gray-200">{snapshotStatus.schedule.mode}</span> {snapshotStatus.schedule.primaryTime}
                            {snapshotStatus.schedule.mode === 'twice_daily' && snapshotStatus.schedule.secondaryTime ? ` e ${snapshotStatus.schedule.secondaryTime}` : ''}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-3 space-y-3">
                      <div className="text-xs uppercase tracking-[0.2em] text-gray-400">Executar agora</div>
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="text-xs text-gray-300">
                          <div className="mb-1 uppercase tracking-[0.2em] text-gray-400">Data do snapshot</div>
                          <input
                            type="date"
                            className="rounded-lg border border-vision-border bg-vision-surface px-3 py-2 text-sm text-gray-100"
                            value={snapshotRunDate}
                            onChange={(e) => setSnapshotRunDate(e.target.value)}
                          />
                        </label>
                        <button
                          onClick={() => handleSnapshotRunNow(snapshotModalTenant)}
                          disabled={actioning !== null || !snapshotRunDate}
                          className="rounded-lg border border-vision-border px-3 py-2 text-xs text-gray-100 hover:border-vision-green disabled:opacity-60"
                        >
                          {actioning === `${snapshotModalTenant.id}-snapshot-run-now` ? 'Enfileirando...' : 'Executar agora'}
                        </button>
                        <div className="text-xs text-gray-400">
                          Enfileira snapshots imediatos para a data escolhida.
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {Object.entries(snapshotStatus.latest).map(([key, value]) => (
                        <div key={key} className="rounded-lg border border-vision-border bg-vision-darkGreen p-2">
                          <div className="uppercase tracking-[0.2em] text-gray-400">{key}</div>
                          <div className="mt-1 text-gray-100">{value || '-'}</div>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-2">
                        <div className="text-gray-400">Pendentes</div>
                        <div className="text-lg font-semibold text-gray-100">{snapshotStatus.jobs.pending}</div>
                      </div>
                      <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-2">
                        <div className="text-gray-400">Processando</div>
                        <div className="text-lg font-semibold text-gray-100">{snapshotStatus.jobs.processing}</div>
                      </div>
                      <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-2">
                        <div className="text-gray-400">Concluidos</div>
                        <div className="text-lg font-semibold text-gray-100">{snapshotStatus.jobs.done}</div>
                      </div>
                      <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-2">
                        <div className="text-gray-400">Falhos</div>
                        <div className="text-lg font-semibold text-vision-activeRed">{snapshotStatus.jobs.failed}</div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-vision-border bg-vision-darkGreen p-3 text-xs text-gray-300">
                      <div>Ultimo sucesso: <span className="text-gray-100">{snapshotStatus.runs.latestSuccessDate || '-'}</span></div>
                      <div>Ultima falha: <span className="text-gray-100">{snapshotStatus.runs.latestFailureDate || '-'}</span></div>
                      {snapshotStatus.runs.latestFailureError && (
                        <div className="mt-1 text-vision-activeRed">Erro: {snapshotStatus.runs.latestFailureError}</div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-400">Sem dados de snapshot.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

